import { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Button,
  Chip,
  Grid,
  Paper,
  Stack,
  Avatar,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Divider,
} from "@mui/material";
import {
  People as PeopleIcon,
  CalendarMonth as CalendarMonthIcon,
  LocationOn as LocationOnIcon,
  Straighten as StraightenIcon,
  AccessTime as AccessTimeIcon,
  Star as StarIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Visibility as VisibilityIcon,
  Pets as PetsIcon,
} from "@mui/icons-material";

/**
 * Dialog for displaying the details of a shared dive
 *
 * @param {Object} props
 * @param {boolean} props.open - Whether the dialog is open
 * @param {Function} props.onClose - Function to call when closing the dialog
 * @param {Object} props.dive - Dive data object
 */
function SharedDiveDetailsDialog({ open, onClose, dive }) {
  const [expanded, setExpanded] = useState(false);
  console.log("Shared Dive dialog - Dive data:", dive);
  if (!dive) return null;

  const toggleExpanded = () => setExpanded(!expanded);
  const formattedDate = dive.dive?.diveDate
    ? new Date(dive.dive.diveDate).toLocaleDateString()
    : "Unknown date";

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="h6">Shared Dive Details</Typography>
          <Chip
            icon={<PeopleIcon />}
            label={`Shared with ${dive.sharedWithCount || 0}`}
            color="primary"
            size="small"
          />
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" gutterBottom>
            {dive.dive.divesite.uddf.divesite.site?.name || "Unnamed Dive Site"}
          </Typography>

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography
                  variant="subtitle2"
                  color="textSecondary"
                  gutterBottom
                >
                  Date & Location
                </Typography>
                <Stack spacing={1}>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <CalendarMonthIcon
                      fontSize="small"
                      sx={{ mr: 1, color: "text.secondary" }}
                    />
                    <Typography>{formattedDate}</Typography>
                  </Box>
                  {dive.dive?.divesite?.uddf.divesite.site.geography.address
                    .city && (
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <LocationOnIcon
                        fontSize="small"
                        sx={{ mr: 1, color: "text.secondary" }}
                      />
                      <Typography>
                        {
                          dive.dive.divesite.uddf.divesite.site.geography
                            .address.city
                        }
                      </Typography>
                    </Box>
                  )}
                </Stack>
              </Paper>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography
                  variant="subtitle2"
                  color="textSecondary"
                  gutterBottom
                >
                  Dive Statistics
                </Typography>
                <Stack spacing={1}>
                  {dive.dive?.diveDuration && (
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <AccessTimeIcon
                        fontSize="small"
                        sx={{ mr: 1, color: "text.secondary" }}
                      />
                      <Typography>{dive.dive.diveDuration} minutes</Typography>
                    </Box>
                  )}
                  {dive.dive?.diveRating && (
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <StarIcon
                        fontSize="small"
                        sx={{ mr: 1, color: "text.secondary" }}
                      />
                      <Typography>{dive.dive.diveRating}/5 rating</Typography>
                    </Box>
                  )}
                  {dive.dive?.visibilityRating && (
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <VisibilityIcon
                        fontSize="small"
                        sx={{ mr: 1, color: "text.secondary" }}
                      />
                      <Typography>
                        {dive.dive.visibilityRating}/5 visibility
                      </Typography>
                    </Box>
                  )}
                </Stack>
              </Paper>
            </Grid>
          </Grid>

          {/* Seen Items Section */}
          {dive.dive?.seenItems && dive.dive.seenItems.length > 0 && (
            <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
              <Typography
                variant="subtitle2"
                color="textSecondary"
                gutterBottom
                sx={{ display: "flex", alignItems: "center" }}
              >
                <PetsIcon fontSize="small" sx={{ mr: 1 }} />
                Sightings
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {dive.dive.seenItems.map((item) => (
                  <Chip
                    key={item._id}
                    label={item.commonName}
                    size="small"
                    color="secondary"
                    variant="outlined"
                    sx={{ mb: 0.5, mr: 0.5 }}
                    title={item.scientificName}
                  />
                ))}
              </Box>

              {/* Display the first sighting details with its description */}
              {dive.dive.seenItems.length > 0 && (
                <Box
                  sx={{ mt: 2, pt: 2, borderTop: "1px solid rgba(0,0,0,0.12)" }}
                >
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      {dive.dive.seenItems[0].imageUrl && (
                        <Box
                          component="img"
                          src={dive.dive.seenItems[0].imageUrl}
                          alt={dive.dive.seenItems[0].commonName}
                          sx={{
                            width: "100%",
                            height: 140,
                            objectFit: "cover",
                            borderRadius: 1,
                          }}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.style.display = "none";
                          }}
                        />
                      )}
                    </Grid>
                    <Grid item xs={12} sm={8}>
                      <Typography variant="subtitle2">
                        {dive.dive.seenItems[0].commonName}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block", fontStyle: "italic", mb: 1 }}
                      >
                        {dive.dive.seenItems[0].scientificName}
                      </Typography>
                      <Typography variant="body2">
                        {dive.dive.seenItems[0].description}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              )}
            </Paper>
          )}

          {dive.dive?.comments && (
            <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
              <Typography
                variant="subtitle2"
                color="textSecondary"
                gutterBottom
              >
                Your Comments
              </Typography>
              <Typography variant="body2">
                {expanded
                  ? dive.dive.comments
                  : dive.dive.comments.length > 150
                  ? dive.dive.comments.substring(0, 150) + "..."
                  : dive.dive.comments}
              </Typography>
              {dive.dive.comments.length > 150 && (
                <Button
                  size="small"
                  onClick={toggleExpanded}
                  sx={{ mt: 1 }}
                  endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                >
                  {expanded ? "Show Less" : "Show More"}
                </Button>
              )}
            </Paper>
          )}

          <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 2 }}>
            Shared With
          </Typography>

          {dive.relatedDives?.length > 0 ? (
            <Grid container spacing={2}>
              {dive.relatedDives.map((relatedDive) => (
                <Grid item xs={12} sm={6} md={4} key={relatedDive._id}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <ListItem disableGutters sx={{ px: 0 }}>
                      <ListItemAvatar>
                        <Avatar src={relatedDive.diver?.profilePicture}>
                          {relatedDive.diver?.name?.[0] || "?"}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={relatedDive.diver?.name || "Another Diver"}
                        secondary={
                          relatedDive.diveDate
                            ? new Date(
                                relatedDive.diveDate
                              ).toLocaleDateString()
                            : "Unknown date"
                        }
                      />
                    </ListItem>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Typography color="textSecondary">
              No divers have contributed to this shared dive yet.
            </Typography>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

export default SharedDiveDetailsDialog;
